// Thin client for the guest-checkout API (backend/server.py, /api/guest/*).
// Every call degrades gracefully: when the backend is unreachable (e.g. a
// static/preview deploy of just the frontend) callers fall back to the
// design's sample-data behavior.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

/* HTTP-CLIENT-01 / UX-LONG-OP-01.
 *
 * Every request is bounded. `fetch` has no timeout of its own, so a request
 * that never answers — a proxy that holds the socket open, a phone that walks
 * out of coverage mid-POST — used to leave the UI waiting forever with a
 * disabled button and no way back. An AbortController closes that.
 *
 * The timeout error is deterministic: code REQUEST_TIMEOUT, status 0,
 * `timeout: true`, and the same message every time, so a caller can branch on
 * it rather than string-matching whatever the browser happened to throw.
 *
 * Retry classification is by method, and it is deliberately conservative.
 * GET and HEAD are idempotent, so a transport failure may be retried once.
 * Nothing else is retried automatically: this API has no idempotency-key
 * contract on its writes, so a retried POST /guest/orders is a second order
 * and a retried cancellation is a second cancellation. A write that failed in
 * an unknown state is surfaced to the caller with `retryable: false` — the
 * customer decides, having been told what happened, which is the same rule the
 * backend applies to provider submissions.
 */
const DEFAULT_TIMEOUT_MS = 15000;
const UPLOAD_TIMEOUT_MS = 60000;   // a photo over a slow uplink is not a hang
const IDEMPOTENT_METHODS = new Set(["GET", "HEAD"]);

export const REQUEST_TIMEOUT = "REQUEST_TIMEOUT";

function timeoutError(ms) {
  const err = new Error(
    `The server did not answer within ${Math.round(ms / 1000)} seconds.`);
  err.code = REQUEST_TIMEOUT;
  err.status = 0;
  err.timeout = true;
  return err;
}

/** fetch with a hard deadline. Rejects with a deterministic timeout error
 *  rather than the browser's own AbortError, which differs across engines. */
async function fetchWithDeadline(url, options, timeoutMs) {
  // AbortSignal.timeout() is not in every supported browser; an explicit
  // controller behaves identically everywhere and is cancellable.
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } catch (e) {
    if (controller.signal.aborted) throw timeoutError(timeoutMs);
    const err = new Error("Could not reach the server.");
    err.code = "NETWORK_UNREACHABLE";
    err.status = 0;
    err.cause = e;
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

/** True only where repeating the call cannot create a second side effect. */
function mayRetry(method) {
  return IDEMPOTENT_METHODS.has((method || "GET").toUpperCase());
}

async function request(path, options = {}) {
  const { timeoutMs = DEFAULT_TIMEOUT_MS, ...init } = options;
  const method = (init.method || "GET").toUpperCase();
  const url = `${BACKEND_URL}/api${path}`;
  const send = () => fetchWithDeadline(url, {
    headers: { "Content-Type": "application/json" },
    ...init,
  }, timeoutMs);

  let res;
  try {
    res = await send();
  } catch (e) {
    // One retry, and only where the method makes it safe. A timed-out write
    // may well have been applied server-side; repeating it is how duplicates
    // get made.
    if (mayRetry(method)) {
      try {
        res = await send();
      } catch (again) {
        again.retryable = true;
        throw again;
      }
    } else {
      e.retryable = false;
      throw e;
    }
  }

  const body = await res.json().catch(() => null);
  if (!res.ok || body === null || typeof body !== "object") {
    // body === null also covers static hosts that SPA-rewrite /api/* to
    // index.html with a 200 — treat that as "no backend".
    // FastAPI errors arrive in two shapes: {detail:{code,message}} and the
    // plain {detail:"..."} string form. Surface the string form as the
    // message so callers never show a bare status code for a refusal the
    // server actually explained.
    const detailMessage = typeof body?.detail === "string"
      ? body.detail
      : body?.detail?.message;
    const err = new Error(detailMessage || `Request failed (${res.status})`);
    err.code = body?.detail?.code || "REQUEST_FAILED";
    err.status = res.ok ? 0 : res.status;
    // A 5xx on an idempotent read is worth offering again; a rejected write is
    // not, and neither is anything the server refused on purpose.
    err.retryable = mayRetry(method) && (res.status >= 500 || res.status === 0);
    throw err;
  }
  return body;
}

/** Component health for the public /status page, plus the two release flags.
 *  Reports what is actually measured — an unconfigured integration says so
 *  rather than showing green, and `production_ready`/`mail_submission_enabled`
 *  come from the blocker register, not from configuration. */
export function getHealth() {
  return request("/health");
}

/** POLICY-TRUST-UI-01. What CareCard knows about each facility's rules, when
 *  it last checked, and where it read them. Public because the honest version
 *  reassures more than a badge would — and it carries no endorsement claim,
 *  because CareCard has none. */
export function getPolicyStatus() {
  return request("/carecard/policy-status");
}

/** The canonical print geometry and its proof gates. Public because it holds
 *  no customer data and because the reserved postal zones are the part most
 *  easily got wrong. */
export function getPrintLayout() {
  return request("/print-layout");
}

/** Upload a photo for the front of a card. Returns { image_id, url, ... }.
 *  `url` is server-relative; imageSrc() turns it into something renderable. */
export async function uploadGuestImage(file) {
  const body = new FormData();
  body.append("file", file);
  // No Content-Type header here on purpose — the browser has to set it so the
  // multipart boundary is included.
  // Longer deadline than an ordinary call, because this one carries a photo,
  // but still bounded — and never retried, because a repeated upload is a
  // second stored file the sweep then has to find.
  const res = await fetchWithDeadline(
    `${BACKEND_URL}/api/guest/uploads`, { method: "POST", body }, UPLOAD_TIMEOUT_MS);
  const parsed = await res.json().catch(() => null);
  if (!res.ok || parsed === null || typeof parsed !== "object" || !parsed.image_id) {
    const err = new Error(parsed?.detail?.message || `Upload failed (${res.status})`);
    err.code = parsed?.detail?.code || "UPLOAD_FAILED";
    err.status = res.ok ? 0 : res.status;
    err.retryable = false;
    throw err;
  }
  return parsed;
}

/** Absolute src for a server-relative image path. */
export function imageSrc(path) {
  return path ? `${BACKEND_URL}${path}` : null;
}

/** The four facilities, carrying whether each is currently accepting mail.
 *  Returns null with no backend so the picker shows every facility rather than
 *  inventing a pause. */
export async function getGuestFacilities() {
  try {
    return await request("/guest/facilities");
  } catch {
    return null;
  }
}

/** Full policy-aware public facility detail. Conflicted/stale facilities remain
 * visible for truthful education but are not treated as orderable. */
export async function getGuestFacilityDetail(slug) {
  try {
    return await request(`/guest/facilities/${encodeURIComponent(slug)}`);
  } catch {
    return null;
  }
}

export function createGuestOrder(order) {
  // V44: send the customer session when one exists so the order is saved to
  // that account. A missing or stale token is ignored server-side — guest
  // checkout never depends on it.
  return customerRequest("/guest/orders", { method: "POST", body: JSON.stringify(order) });
}

// ── Giveaway (V37) ───────────────────────────────────────────────────────────
// The owner's free-card offer: the first 10 submissions in the campaign are
// free. Status is read before the form is offered, so the page never claims
// a free card that is no longer there; the submit endpoint re-checks the cap
// server-side and answers GIVEAWAY_FULL / GIVEAWAY_DUPLICATE when it moves.

export function getGiveawayStatus() {
  return request("/guest/giveaway/status");
}

export function submitGiveawayOrder(order) {
  return request("/guest/giveaway/orders", {
    method: "POST",
    body: JSON.stringify(order),
  });
}

/** POST /api/validate-booking — checks a booking number against a facility.
 *
 *  The backend is built in parallel, so this degrades gracefully: a 404 (no
 *  such endpoint yet) or an unreachable server resolves to {valid: null} —
 *  "couldn't check" — instead of throwing, and the flow never crashes.
 *  Callers treat valid:null as "proceed without the check".
 */
export async function validateBooking(facilityId, bookingId, locale = "en") {
  try {
    return await request("/validate-booking", {
      method: "POST",
      body: JSON.stringify({ facility_id: facilityId, booking_id: bookingId, locale }),
    });
  } catch (e) {
    if (e.status === 404 || e.status === 0) {
      return { valid: null, normalized: null, message: "Booking validation is unavailable right now." };
    }
    throw e;
  }
}

export function reconcileGuestCheckout(sessionId) {
  return request(`/guest/checkout/${encodeURIComponent(sessionId)}`);
}

export function getGuestOrder(publicId) {
  return request(`/guest/orders/${encodeURIComponent(publicId)}`);
}

/** Protected order detail. WEB-02: the capability travels in a header, having
 *  arrived in the URL fragment — never in the path or a query string, where it
 *  would land in access logs and referrers. */
export function getPrivateOrder(publicId, capability) {
  return request(`/guest/orders/${encodeURIComponent(publicId)}/private`, {
    headers: { "Content-Type": "application/json", "X-Order-Capability": capability },
  });
}

/** Ask for a fresh private link. Answers identically whether or not the order
 *  and email match, so it cannot be used to test order ids. */
export function requestPrivateAccess(publicId, email) {
  return request(`/guest/orders/${encodeURIComponent(publicId)}/private-access`, {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

export function cancelGuestOrder(publicId, email) {
  return request(`/guest/orders/${encodeURIComponent(publicId)}/cancel`, {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

// ── Privacy: data deletion ───────────────────────────────────────────────────
// The form used to advance client-side state and call nothing. Erasure is a
// statutory right, so a screen saying "we've accepted your request" when
// nothing was submitted is an unanswered legal request with a receipt.

export function requestDeletion(email) {
  return request("/guest/privacy/deletion-request", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

export function confirmDeletion(token) {
  return request("/guest/privacy/deletion-confirm", {
    method: "POST",
    body: JSON.stringify({ token }),
  });
}

export function joinGuestWaitlist(email) {
  return request("/guest/waitlist", { method: "POST", body: JSON.stringify({ email }) });
}

export function getWaitlistCount() {
  return request("/guest/waitlist/count");
}

export function submitContact({ topic, email, orderId, message }) {
  return request("/guest/contact", {
    method: "POST",
    body: JSON.stringify({ topic, email, order_id: orderId || null, message }),
  });
}

// ── Moderation (operator-only) ───────────────────────────────────────────────
// The token is held in sessionStorage so it survives a reload but not a closed
// tab. It is a shared operator secret, not a user login — see MASTER_SPEC §16.

const MOD_TOKEN_KEY = "cc-moderation-token";

export function getModerationToken() {
  try {
    return sessionStorage.getItem(MOD_TOKEN_KEY) || "";
  } catch {
    return "";
  }
}

export function setModerationToken(token) {
  try {
    if (token) sessionStorage.setItem(MOD_TOKEN_KEY, token);
    else sessionStorage.removeItem(MOD_TOKEN_KEY);
  } catch {
    /* private browsing — the token simply won't persist */
  }
}

function moderationRequest(path, options) {
  const headers = { "Content-Type": "application/json", "X-Moderation-Token": getModerationToken() };
  // B-26 cutover: operational routes now require the per-admin identity-plane
  // bearer token. Attach it whenever an admin is signed in (Employees section);
  // the legacy moderation token is kept for the few routes that still take it.
  const identityToken = getAdminIdentityToken();
  if (identityToken) headers["Authorization"] = `Bearer ${identityToken}`;
  return request(path, {
    ...options,
    headers,
  });
}

export function getModerationQueue(state = "pending") {
  return moderationRequest(`/guest/moderation/queue?state=${encodeURIComponent(state)}`);
}

export function approveModeration(publicId, { decidedBy, note }) {
  return moderationRequest(`/guest/moderation/${encodeURIComponent(publicId)}/approve`, {
    method: "POST",
    body: JSON.stringify({ decided_by: decidedBy, note: note || null }),
  });
}

export function rejectModeration(publicId, { decidedBy, reason, reasonCode, note, blockImage }) {
  return moderationRequest(`/guest/moderation/${encodeURIComponent(publicId)}/reject`, {
    method: "POST",
    body: JSON.stringify({
      decided_by: decidedBy,
      reason,
      reason_code: reasonCode || null,
      note: note || null,
      block_image: Boolean(blockImage),
    }),
  });
}

// Canonical BSO moderation reason codes (backend/server.py BSO_MODERATION_REASON_CODES).
// A reviewer records one of these so every rejection is attributable to the
// BSO rule it violated; the free-text reason still carries the specifics.
export const MODERATION_REASON_CODES = [
  { code: "PHOTO_NUDITY", label: "Photo: nudity / explicit content" },
  { code: "PHOTO_WEAPONS", label: "Photo: weapons depicted" },
  { code: "PHOTO_INMATE_LIKENESS", label: "Photo: likeness of an incarcerated person" },
  { code: "MESSAGE_CODED", label: "Message: coded message" },
  { code: "MESSAGE_GANG_REFERENCE", label: "Message: gang reference" },
];

// ── Recipient lookup ─────────────────────────────────────────────────────────

/** Whether recipient search can work. The compose step asks before rendering a
 *  search box, so nobody is offered a search that cannot return anything.
 *  (The /guest/inmate-search path is the staged backend's endpoint name.) */
export async function getRecipientSearchStatus() {
  try {
    return await request("/guest/inmate-search/status");
  } catch {
    return { available: false, provider: null };
  }
}

/** @deprecated alias — use getRecipientSearchStatus. */
export const getInmateSearchStatus = getRecipientSearchStatus;

export function searchRecipients(query, facilitySlug) {
  const params = new URLSearchParams({ q: query });
  if (facilitySlug) params.set("facility", facilitySlug);
  return request(`/guest/inmate-search?${params.toString()}`);
}

/** @deprecated alias — use searchRecipients. */
export const searchInmates = searchRecipients;

// ── Operator order console ───────────────────────────────────────────────────
// Same operator credential as the moderation queue.

export function getAdminOrders({ q = "", status = "", facility = "", page = 1 } = {}) {
  const params = new URLSearchParams({ page: String(page) });
  if (q) params.set("q", q);
  if (status) params.set("status", status);
  if (facility) params.set("facility", facility);
  return moderationRequest(`/guest/admin/orders?${params.toString()}`);
}

export function getAdminOrder(publicId) {
  return moderationRequest(`/guest/admin/orders/${encodeURIComponent(publicId)}`);
}

export function getAdminStats() {
  return moderationRequest("/guest/admin/stats");
}

// ── Admin dashboard: print/mail pipeline, rejections, credit liability, support ─
// Operator-only (same moderation token). These endpoints are built in parallel
// on the backend — every caller must tolerate 404 / network failure and
// render a friendly "API not yet available" state, never throw.

/** Print/mail pipeline board: per-stage counts plus the orders in each stage. */
export function getAdminPipeline() {
  return moderationRequest("/admin/orders/pipeline");
}

/** BSO rejection tracker: counts by facility and by reason, plus recent rows. */
export function getAdminRejections() {
  return moderationRequest("/admin/rejections");
}

/** Outstanding credit liability: photo credits + referral $5 credits. */
export function getAdminCreditLiability() {
  return moderationRequest("/admin/credit-liability");
}

/** Support lookup: find one order by sender email and/or public order id. */
export function getAdminOrderLookup({ email = "", orderId = "" } = {}) {
  const params = new URLSearchParams();
  if (email) params.set("email", email);
  if (orderId) params.set("order_id", orderId);
  const qs = params.toString();
  return moderationRequest(`/admin/orders/lookup${qs ? `?${qs}` : ""}`);
}

/** Support actions. The admin dashboard currently mocks these (test mode):
 *  they exist so the backend contract is wired when the dashboard goes live. */
// Legacy name kept for callers; the resend-confirmation route never existed
// server-side. It now targets the real resend-email endpoint (A8).
export function adminResendConfirmation(publicId) {
  return adminResendOrderEmail(publicId, "order_received");
}

// ── Customer account API (A1) ─────────────────────────────────────────────
const CUSTOMER_TOKEN_KEY = "cc-customer-token";

export function getCustomerToken() {
  try { return localStorage.getItem(CUSTOMER_TOKEN_KEY) || ""; } catch { return ""; }
}
export function setCustomerToken(token) {
  try {
    if (token) localStorage.setItem(CUSTOMER_TOKEN_KEY, token);
    else localStorage.removeItem(CUSTOMER_TOKEN_KEY);
  } catch {}
}
function customerRequest(path, options) {
  const token = getCustomerToken();
  return request(path, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { "Authorization": `Bearer ${token}` } : {}),
    },
  });
}
export function customerRegister({ email, password, firstName, lastName, phone, preferredLanguage }) {
  return request("/auth/register", {
    method: "POST",
    body: JSON.stringify({
      email, password,
      first_name: firstName, last_name: lastName,
      phone: phone || undefined,
      preferred_language: preferredLanguage || undefined,
    }),
  });
}
export function customerLogin(email, password) {
  return request("/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
}
export function customerLogout() {
  const p = customerRequest("/auth/logout", { method: "POST" }).catch(() => ({}));
  setCustomerToken("");
  return p;
}
export function customerMe() { return customerRequest("/auth/me"); }
export function customerConfirmDeletion(token) {
  return request("/auth/me/deletion-confirm", { method: "POST", body: JSON.stringify({ token }) });
}
export function adminCustomerList({ q = "", status = "", page = 1 } = {}) {
  const params = new URLSearchParams();
  if (q) params.set("q", q);
  if (status) params.set("status", status);
  if (page > 1) params.set("page", String(page));
  const qs = params.toString();
  return moderationRequest(`/guest/admin/customers/list${qs ? `?${qs}` : ""}`);
}
export function adminCustomerDetail(userId) {
  return moderationRequest(`/guest/admin/customers/${encodeURIComponent(userId)}`);
}
export function customerVerifyEmail(token) {
  return request("/auth/verify-email", { method: "POST", body: JSON.stringify({ token }) });
}
export function customerResendVerification(email) {
  return request("/auth/resend-verification", { method: "POST", body: JSON.stringify({ email }) });
}
export function customerForgotPassword(email) {
  return request("/auth/forgot-password", { method: "POST", body: JSON.stringify({ email }) });
}
export function customerResetPassword(token, newPassword) {
  return request("/auth/reset-password", {
    method: "POST",
    body: JSON.stringify({ token, new_password: newPassword }),
  });
}
export function customerUpdateProfile(patch) {
  const body = {};
  if (patch.firstName) body.first_name = patch.firstName;
  if (patch.lastName) body.last_name = patch.lastName;
  if (patch.phone !== undefined) body.phone = patch.phone;
  if (patch.preferredLanguage) body.preferred_language = patch.preferredLanguage;
  return customerRequest("/auth/profile", { method: "PUT", body: JSON.stringify(body) });
}
export function customerMyOrders() { return customerRequest("/auth/me/orders"); }
export function customerMyEvents() { return customerRequest("/auth/me/events"); }
export function customerExportData() { return customerRequest("/auth/me/export"); }
export function customerRequestDeletion() {
  return customerRequest("/auth/me/deletion-request", { method: "POST" });
}
export function customerGetNotifyPrefs() { return customerRequest("/auth/me/notification-prefs"); }
export function customerSaveNotifyPrefs(prefs) {
  return customerRequest("/auth/me/notification-prefs", {
    method: "PUT", body: JSON.stringify(prefs),
  });
}

// ── Account reminders (V37) ─────────────────────────────────────────────────
// Per-recipient reminder configs on the signed-in account: a birthday
// reminder (driven by the recipient profile's birthday) or a follow-up on a
// chosen date, emailed `lead_days` ahead. Customer bearer auth, like every
// other /auth/me/* call above.

export function customerGetReminders() {
  return customerRequest("/auth/me/reminders");
}

export function customerCreateReminder({ profileId, kind, leadDays, dueDate, enabled }) {
  const body = {
    profile_id: profileId,
    kind,
    lead_days: leadDays,
  };
  if (dueDate !== undefined) body.due_date = dueDate || null;
  if (enabled !== undefined) body.enabled = Boolean(enabled);
  return customerRequest("/auth/me/reminders", {
    method: "POST", body: JSON.stringify(body),
  });
}

export function customerUpdateReminder(id, patch) {
  const body = {};
  if (patch.leadDays !== undefined) body.lead_days = patch.leadDays;
  if (patch.dueDate !== undefined) body.due_date = patch.dueDate || null;
  if (patch.enabled !== undefined) body.enabled = Boolean(patch.enabled);
  return customerRequest(`/auth/me/reminders/${encodeURIComponent(id)}`, {
    method: "PATCH", body: JSON.stringify(body),
  });
}

export function customerDeleteReminder(id) {
  return customerRequest(`/auth/me/reminders/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

// ── Saved recipient profiles (V37) ──────────────────────────────────────────
// The account's saved recipients live behind the saved-recipient routes
// (the request paths below are the API's long-standing route names for the
// resource; the UI always says "loved one" / "recipient"). Customer bearer
// auth via get_current_user, same token as the /auth/* calls.
// birthday_month / birthday_day are optional and nullable — they exist so
// birthday reminders know the day.

export function getSavedRecipientProfiles() {
  return customerRequest("/inmates");
}

export function createSavedRecipientProfile({ fullName, arrestNumber, facilityId, notes, birthdayMonth, birthdayDay }) {
  return customerRequest("/inmates", {
    method: "POST",
    body: JSON.stringify({
      full_name: fullName,
      arrest_number: arrestNumber,
      facility_id: facilityId,
      notes: notes || null,
      birthday_month: birthdayMonth ?? null,
      birthday_day: birthdayDay ?? null,
    }),
  });
}

export function updateSavedRecipientProfile(id, { fullName, arrestNumber, facilityId, notes, birthdayMonth, birthdayDay }) {
  return customerRequest(`/inmates/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: JSON.stringify({
      full_name: fullName,
      arrest_number: arrestNumber,
      facility_id: facilityId,
      notes: notes || null,
      birthday_month: birthdayMonth ?? null,
      birthday_day: birthdayDay ?? null,
    }),
  });
}
export function orderClaimRequest(publicId, email) {
  return request("/guest/orders/claim/request", {
    method: "POST", body: JSON.stringify({ public_id: publicId, email }),
  });
}
export function orderClaimConfirm(token) {
  return customerRequest("/guest/orders/claim/confirm", {
    method: "POST", body: JSON.stringify({ token }),
  });
}

// ── Admin console additions (A5/A6/A7/A8) ─────────────────────────────────
export function adminPolicyReviews(severity) {
  const q = severity ? `?severity=${encodeURIComponent(severity)}` : "";
  return moderationRequest(`/guest/admin/policy/reviews${q}`);
}
export function adminPolicyDecide(itemId, approve, reason) {
  // P_FACILITY_RULE_CHANGE is high-risk: fresh auth + X-Admin-Reason header
  // are enforced server-side. identityRequest attaches the reason header.
  return identityRequest(`/guest/admin/policy/reviews/${encodeURIComponent(itemId)}/decide`, {
    method: "POST",
    reason: reason || "",
    body: JSON.stringify({ approve: !!approve, reason: reason || "" }),
  });
}
export function adminResendOrderEmail(publicId, template) {
  return moderationRequest(`/guest/admin/orders/${encodeURIComponent(publicId)}/resend-email`, {
    method: "POST",
    body: JSON.stringify({ template: template || "order_received" }),
  });
}
export function adminExportOrders({ dateFrom, dateTo, status, limit, reason }) {
  // P_EXPORT is high-risk: reason header required.
  return identityRequest("/guest/admin/export/orders", {
    method: "POST",
    reason: reason || "",
    body: JSON.stringify({
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
      status: status || undefined,
      limit: limit || 5000,
    }),
  });
}
export function adminCustomerLookup(email) {
  return moderationRequest(`/guest/admin/customers?email=${encodeURIComponent(email)}`);
}
export function adminCustomerSuspend(userId, reason) {
  // P_CUSTOMER_SUSPEND is high-risk: reason header required.
  return identityRequest(`/guest/admin/customers/${encodeURIComponent(userId)}/suspend`, {
    method: "POST",
    reason: reason || "",
    body: JSON.stringify({ reason }),
  });
}
export function adminCustomerRestore(userId, reason) {
  return identityRequest(`/guest/admin/customers/${encodeURIComponent(userId)}/restore`, {
    method: "POST",
    reason: reason || "",
  });
}
export function adminCustomerRevokeSessions(userId, reason) {
  return identityRequest(`/guest/admin/customers/${encodeURIComponent(userId)}/sessions/revoke`, {
    method: "POST",
    reason: reason || "",
  });
}

// ── Giveaway admin (V37) ────────────────────────────────────────────────────
// The giveaway list and the comp action sit on the admin identity plane
// (bearer session), not the shared moderation token. Comping is a
// money-moving decision, so it follows the high-risk pattern: the reason
// travels both as the X-Admin-Reason header (identityRequest) and in the
// JSON body, and the server attributes it in the audit log.

export function getAdminGiveawayOrders() {
  return identityRequest("/guest/admin/giveaway/orders");
}

export function compAdminOrder(publicId, reason) {
  return identityRequest(`/guest/admin/orders/${encodeURIComponent(publicId)}/comp`, {
    method: "POST",
    reason: reason || "",
    body: JSON.stringify({ reason: reason || "" }),
  });
}

export function adminIssueCredit(publicId, credits) {
  return moderationRequest(`/admin/orders/${encodeURIComponent(publicId)}/issue-credit`, {
    method: "POST",
    body: JSON.stringify({ credits }),
  });
}

export function adminCancelScheduled(publicId) {
  return moderationRequest(`/admin/orders/${encodeURIComponent(publicId)}/cancel-scheduled`, {
    method: "POST",
  });
}

// ── Email outbox ─────────────────────────────────────────────────────────────
// With no mail provider configured every message queues as `pending`. That is
// only useful if an operator can see the backlog, so the console reads it.

export function getAdminEmails({ status = "" } = {}) {
  const params = new URLSearchParams();
  if (status) params.set("status", status);
  const qs = params.toString();
  return moderationRequest(`/guest/admin/emails${qs ? `?${qs}` : ""}`);
}

// ── Provider events (WEB-08 /admin/providers) ───────────────────────────────
// Read-only provider event ledger (B-27). The staged backend supports an
// optional `provider` filter (e.g. stripe, postgrid) plus pagination.
export function getAdminProviderEvents({ provider = "", limit = 100, offset = 0 } = {}) {
  const params = new URLSearchParams();
  if (provider) params.set("provider", provider);
  if (limit) params.set("limit", String(limit));
  if (offset) params.set("offset", String(offset));
  const qs = params.toString();
  return moderationRequest(`/guest/admin/provider-events${qs ? `?${qs}` : ""}`);
}

// ── Alert history (WEB-08 /admin/alerts) ────────────────────────────────────
export function getAdminAlertHistory() {
  return moderationRequest("/guest/admin/alerts/history");
}

// ── Audit log, financials, promos ────────────────────────────────────────────
// All three sections previously rendered invented data. These are the real
// collections behind them.

export function getAdminAudit({ action = "" } = {}) {
  const params = new URLSearchParams();
  if (action) params.set("action", action);
  const qs = params.toString();
  return moderationRequest(`/guest/admin/audit${qs ? `?${qs}` : ""}`);
}

/** Feature flags. Public read — the send flow asks before rendering a checkout
 *  the server would refuse, rather than 503-ing somebody at the pay button. */
export async function getPublicFlags() {
  try {
    return await request("/guest/flags");
  } catch {
    // No backend (static demo). Assume nothing is switched off rather than
    // showing a maintenance notice that isn't real.
    return null;
  }
}

export function getAdminRoutes() {
  return moderationRequest("/guest/admin/routes");
}

export function getAdminFacilities() {
  return moderationRequest("/guest/admin/facilities");
}

/** Facility Launch Console. Read-only by design: launch state is computed from
 *  policy, watch and gate evidence, so there is nothing here to POST. */
export function getLaunchConsole() {
  return moderationRequest("/carecard/launch-console");
}

export function setAdminFacility(slug, { suspended, decidedBy, reason }) {
  return moderationRequest(`/guest/admin/facilities/${encodeURIComponent(slug)}`, {
    method: "POST",
    body: JSON.stringify({ suspended, decided_by: decidedBy, reason: reason || null }),
  });
}

export function getAdminFlags() {
  return moderationRequest("/guest/admin/flags");
}

export function setAdminFlag(key, { enabled, decidedBy, reason }) {
  return moderationRequest(`/guest/admin/flags/${encodeURIComponent(key)}`, {
    method: "POST",
    body: JSON.stringify({ enabled, decided_by: decidedBy, reason: reason || null }),
  });
}

export function getAdminTickets({ status = "" } = {}) {
  const params = new URLSearchParams();
  if (status) params.set("status", status);
  const qs = params.toString();
  return moderationRequest(`/guest/admin/tickets${qs ? `?${qs}` : ""}`);
}

export function updateAdminTicket(referenceId, { status, decidedBy }) {
  return moderationRequest(`/guest/admin/tickets/${encodeURIComponent(referenceId)}`, {
    method: "POST",
    body: JSON.stringify({ status, decided_by: decidedBy }),
  });
}

export function getAdminScheduled() {
  return moderationRequest("/guest/admin/scheduled");
}

export function getAdminFinancials() {
  return moderationRequest("/guest/admin/financials");
}

export function getAdminPromos() {
  return moderationRequest("/guest/admin/promos");
}

export function flushAdminEmails() {
  return moderationRequest("/guest/admin/emails/flush", { method: "POST" });
}

// ── Admin task manager ─────────────────────────────────────────────────────
// Operator-only recurring task lists. The backend is built in parallel — every
// caller must tolerate 404 / network failure and render a friendly
// "API not yet available" state, never throw (see callAdmin).

/** Task manager: recurring series, materialised instances, and counts. */
export function getAdminTasks() {
  return moderationRequest("/guest/admin/tasks");
}

/** Generate upcoming instances for active recurring series. */
export function materializeAdminTasks() {
  return moderationRequest("/guest/admin/tasks/materialize", { method: "POST" });
}

/** Create a recurring (or one-off / manual) task series. */
export function createAdminTaskSeries(series) {
  return moderationRequest("/guest/admin/tasks/series", {
    method: "POST",
    body: JSON.stringify(series),
  });
}

/** Update a task series (partial fields). */
export function updateAdminTaskSeries(seriesId, patch) {
  return moderationRequest(`/guest/admin/tasks/series/${encodeURIComponent(seriesId)}`, {
    method: "PUT",
    body: JSON.stringify(patch),
  });
}

/** Delete a task series and its scheduled instances. */
export function deleteAdminTaskSeries(seriesId) {
  return moderationRequest(`/guest/admin/tasks/series/${encodeURIComponent(seriesId)}`, {
    method: "DELETE",
  });
}

/** One-tap manual trigger: create a due-today instance for a manual series. */
export function triggerAdminTaskSeries(seriesId) {
  return moderationRequest(`/guest/admin/tasks/series/${encodeURIComponent(seriesId)}/trigger`, {
    method: "POST",
  });
}

/** Mark a task instance done. Attribution (who completed it) comes from the
 *  server-side operator session — no name is sent. */
export function completeAdminTaskInstance(instanceId) {
  return moderationRequest(`/guest/admin/tasks/instances/${encodeURIComponent(instanceId)}/complete`, {
    method: "POST",
  });
}

/** Reopen a completed task instance. */
export function reopenAdminTaskInstance(instanceId) {
  return moderationRequest(`/guest/admin/tasks/instances/${encodeURIComponent(instanceId)}/reopen`, {
    method: "POST",
  });
}


// Existing public localization override endpoint (EN / ES / HT).
export function getPublicTranslations(language) {
  return request(`/translations/${encodeURIComponent(language)}`);
}

// ── Draft84 privacy-minimized growth & learning ─────────────────────────────
const GROWTH_TOKEN_KEY = "cc-growth-token";
const GROWTH_ADMIN_TOKEN_KEY = "cc-growth-admin-token";

export function getGrowthToken() { try { return sessionStorage.getItem(GROWTH_TOKEN_KEY) || ""; } catch { return ""; } }
export function setGrowthToken(token) { try { token ? sessionStorage.setItem(GROWTH_TOKEN_KEY, token) : sessionStorage.removeItem(GROWTH_TOKEN_KEY); } catch {} }
export function getGrowthAdminToken() { try { return sessionStorage.getItem(GROWTH_ADMIN_TOKEN_KEY) || ""; } catch { return ""; } }
export function setGrowthAdminToken(token) { try { token ? sessionStorage.setItem(GROWTH_ADMIN_TOKEN_KEY, token) : sessionStorage.removeItem(GROWTH_ADMIN_TOKEN_KEY); } catch {} }

function growthRead(view) {
  return request(`/guest/growth/admin/${view}`, { headers: { "Content-Type": "application/json", "X-Growth-Token": getGrowthToken() } });
}
export const getGrowthOverview = () => growthRead("overview");
export const getGrowthBeta = () => growthRead("beta");
export const getGrowthAcquisition = () => growthRead("acquisition");
export const getGrowthFunnel = () => growthRead("funnel");
export const getGrowthRetention = () => growthRead("retention");
export const getGrowthFacilities = () => growthRead("facilities");
export const getGrowthPartners = () => growthRead("partners");
export const getGrowthReferrals = () => growthRead("referrals");
export const getGrowthExperiments = () => growthRead("experiments");
export const getGrowthFeedback = () => growthRead("feedback");
export const getGrowthEconomics = () => growthRead("economics");
export const getGrowthDataQuality = () => growthRead("data-quality");
export const getGrowthDay25 = () => growthRead("day25");

export function updateGrowthBeta(payload) {
  return request("/guest/growth/admin/beta/config", {
    method: "PUT", headers: { "Content-Type": "application/json", "X-Growth-Admin-Token": getGrowthAdminToken() },
    body: JSON.stringify(payload),
  });
}

export function captureAttribution(payload) {
  return request("/guest/growth/attribution", { method: "POST", body: JSON.stringify(payload) });
}
export function captureGrowthEvent(payload) {
  return request("/guest/growth/events", { method: "POST", body: JSON.stringify(payload) });
}
export function getGrowthExperimentAssignment(experimentId, subjectId) {
  return request(`/guest/growth/experiments/${encodeURIComponent(experimentId)}/assignment`, {
    method: "POST", body: JSON.stringify({ subject_id: subjectId }),
  });
}
export function recordGrowthExperimentConversion(experimentId, subjectId, metric) {
  return request(`/guest/growth/experiments/${encodeURIComponent(experimentId)}/conversion`, {
    method: "POST", body: JSON.stringify({ subject_id: subjectId, metric }),
  });
}

const ATTR_SESSION_KEY = "cc-growth-session-v1";
export function getGrowthSessionId() {
  try { return sessionStorage.getItem(ATTR_SESSION_KEY) || ""; } catch { return ""; }
}
export function setGrowthSessionId(value) {
  try {
    value ? sessionStorage.setItem(ATTR_SESSION_KEY, value) : sessionStorage.removeItem(ATTR_SESSION_KEY);
    if (value && typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("carecard:growth-session-ready", { detail: { sessionId: value } }));
    }
  } catch {}
}

export function createCustomerReferral(publicId, capability) {
  return request(`/guest/growth/orders/${encodeURIComponent(publicId)}/referral`, {
    method: "POST", headers: { "Content-Type": "application/json", "X-Order-Capability": capability },
  });
}

export function recordGrowthAdSpend(payload) {
  return request("/guest/growth/admin/ad-spend", {
    method: "POST", headers: { "Content-Type": "application/json", "X-Growth-Admin-Token": getGrowthAdminToken() },
    body: JSON.stringify(payload),
  });
}

export function requestUnsupportedFacility(payload) {
  return request("/guest/growth/facility-demand", { method: "POST", body: JSON.stringify(payload) });
}
export function submitPostFulfillmentFeedback(publicId, capability, payload) {
  return request(`/guest/growth/orders/${encodeURIComponent(publicId)}/feedback`, {
    method: "POST", headers: { "Content-Type": "application/json", "X-Order-Capability": capability }, body: JSON.stringify(payload),
  });
}

// ── Admin identity plane (employee management) ───────────────────────────────
// Separate auth plane from the operator moderation token: the admin identity
// endpoints (backend/server.py /guest/admin/identity/*) use a bearer session
// token from /guest/admin/identity/login, with server-verified TOTP MFA.
// High-risk mutations (account create/update/delete) additionally require a
// fresh-auth elevate and an X-Admin-Reason header (ADM-15).

const ADMIN_IDENTITY_TOKEN_KEY = "cc-admin-identity-token";

export function getAdminIdentityToken() {
  try { return sessionStorage.getItem(ADMIN_IDENTITY_TOKEN_KEY) || ""; } catch { return ""; }
}

export function setAdminIdentityToken(token) {
  try {
    if (token) sessionStorage.setItem(ADMIN_IDENTITY_TOKEN_KEY, token);
    else sessionStorage.removeItem(ADMIN_IDENTITY_TOKEN_KEY);
  } catch { /* private browsing — the token simply won't persist */ }
}

function identityRequest(path, options = {}) {
  const { reason, ...rest } = options;
  const headers = { "Content-Type": "application/json" };
  const token = getAdminIdentityToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;
  if (reason) headers["X-Admin-Reason"] = reason;
  return request(path, { ...rest, headers: { ...headers, ...(rest.headers || {}) } });
}

export function adminIdentityLogin({ email, password }) {
  return request("/guest/admin/identity/login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
}

export function adminIdentityMfaVerify({ code }) {
  return identityRequest("/guest/admin/identity/mfa/verify", {
    method: "POST",
    body: JSON.stringify({ code }),
  });
}

export function adminIdentityElevate({ password }) {
  return identityRequest("/guest/admin/identity/elevate", {
    method: "POST",
    body: JSON.stringify({ password }),
  });
}

export function adminIdentityMe() {
  return identityRequest("/guest/admin/identity/me");
}

export function adminIdentityListAccounts() {
  return identityRequest("/guest/admin/identity/accounts");
}

export function adminIdentityCreateAccount({ email, password, roles, displayName }, { reason }) {
  return identityRequest("/guest/admin/identity/accounts", {
    method: "POST",
    reason,
    body: JSON.stringify({
      email,
      password,
      roles,
      display_name: displayName || null,
    }),
  });
}

export function adminIdentityUpdateAccount(accountId, { displayName, roles, active }, { reason }) {
  const body = {};
  if (displayName !== undefined) body.display_name = displayName;
  if (roles !== undefined) body.roles = roles;
  if (active !== undefined) body.active = active;
  return identityRequest(`/guest/admin/identity/accounts/${encodeURIComponent(accountId)}`, {
    method: "PATCH",
    reason,
    body: JSON.stringify(body),
  });
}

export function adminIdentityDeleteAccount(accountId, { reason }) {
  return identityRequest(`/guest/admin/identity/accounts/${encodeURIComponent(accountId)}`, {
    method: "DELETE",
    reason,
  });
}

export function adminIdentityLogout() {
  return identityRequest("/guest/admin/identity/logout", { method: "POST" });
}

/** Human-readable text for admin-identity API errors (409 guard rails etc.).
 *  request() already folds body.detail.message into err.message and
 *  body.detail.code into err.code. */
export function adminIdentityErrorText(err) {
  if (!err) return "Something went wrong.";
  if (typeof err.message === "string" && err.message && !/^Request failed/.test(err.message)) {
    return err.message;
  }
  if (err.status === 401) return "That sign-in didn't work. Check the email and password.";
  if (err.status === 403) return "This account doesn't have permission for that.";
  if (err.status === 409) return "That change isn't allowed.";
  return "Something went wrong. Try again.";
}
